/**
 * SelfOnlyDashboard - SELF_ONLY権限（社員）向けダッシュボード
 *
 * モバイルアプリのホームに合わせ、縦(portrait)配置・会社別表示設定(entitlement)対応。
 * - 回答ボタンは GB人財 / GBなび の2つ。会社設定 useCompanySettings の hasJinzai / hasNavi で出し分ける。
 * - 横向きゲート（「横向きにしてください」）は出さない。回答フロー自体も縦(portrait)対応。
 * - 個人レポート表示ボタンは表示しない（現フェーズ方針）。
 * - 回答は縦・セッション認証の専用ルート（/answer/gb74, /answer/terasu）へ遷移する。
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { useCompanySettings } from '../../../shared/hooks/useCompanySettings';

const BLUE = '#1a56db';
const INK = '#171b23';
const SUB = '#586173';

/** 回答サービスのカード（縦配置・タップ44px以上）。 */
const AnswerCard: React.FC<{
  title: string;
  description: string;
  onClick: () => void;
}> = ({ title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      background: '#fff',
      border: '1px solid #e4e8f0',
      borderRadius: 14,
      padding: '18px 18px',
      marginBottom: 14,
      boxShadow: '0 1px 2px rgba(20,30,50,.04), 0 4px 14px rgba(20,30,50,.05)',
      cursor: 'pointer',
    }}
  >
    <div style={{ fontSize: 16, fontWeight: 700, color: INK, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 13, color: SUB, marginBottom: 12 }}>{description}</div>
    <span
      style={{
        display: 'inline-block',
        background: BLUE,
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
        padding: '10px 20px',
        borderRadius: 10,
        minHeight: 44,
        lineHeight: '24px',
      }}
    >
      回答する
    </span>
  </button>
);

export const SelfOnlyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const settings = useCompanySettings();

  // ユーザー名の表示（氏名を優先、なければメールアドレスの@前、最後にゲスト）
  const displayName = user?.name || user?.email?.split('@')[0] || 'ゲスト';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const anyAnswerable = settings.hasJinzai || settings.hasNavi;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f6f8fb',
        fontFamily: 'system-ui, -apple-system, "Hiragino Kaku Gothic ProN", sans-serif',
      }}
    >
      {/* 上部バー（ログアウト） */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '12px 16px',
        }}
      >
        <button
          type="button"
          onClick={handleLogout}
          aria-label="ログアウト"
          style={{
            background: 'transparent',
            border: '1px solid #d3dae6',
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 13,
            color: SUB,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          ログアウト
        </button>
      </div>

      {/* メインコンテンツ（縦配置） */}
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '8px 16px 48px' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: INK, margin: '8px 0 20px' }}>
          こんにちは、{displayName} さん
        </h1>

        {settings.hasJinzai && (
          <AnswerCard
            title="GB人財 診断に回答する"
            description="あなたの思考タイプを診断します（約74問）。"
            onClick={() => navigate('/answer/gb74')}
          />
        )}

        {settings.hasNavi && (
          <AnswerCard
            title="GBなび に回答する"
            description="今週の状態を3択で回答します（毎週2問）。"
            onClick={() => navigate('/answer/terasu')}
          />
        )}

        {!anyAnswerable && (
          <p style={{ fontSize: 14, color: SUB, textAlign: 'center', marginTop: 32 }}>
            現在、回答できる診断はありません。管理者にお問い合わせください。
          </p>
        )}
      </main>
    </div>
  );
};

export default SelfOnlyDashboard;
